export default function TutorialPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
            How to Use the Family Album
          </h1>
          <div className="w-24 h-1 bg-gradient-to-r from-primary-500 to-accent-500 mx-auto rounded-full"></div>
          <p className="mt-6 text-xl text-gray-600 max-w-2xl mx-auto">
            Everything you need to know to share memories, connect with family, and make the most of our photo album
          </p>
        </div>

        {/* Quick Start */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl shadow-lg p-8 mb-12 border border-blue-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span>🚀</span> Quick Start
          </h2>
          <p className="text-gray-700 mb-4">
            Welcome! This is a private space for our family to share photos and memories. Here's the basics:
          </p>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start gap-2">
              <span className="text-green-500 font-bold">✓</span>
              <span><strong>Upload photos</strong> using the blue Upload button</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500 font-bold">✓</span>
              <span><strong>View photos</strong> by clicking on any thumbnail</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500 font-bold">✓</span>
              <span><strong>Like and comment</strong> to share your memories</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500 font-bold">✓</span>
              <span><strong>Check the bell icon</strong> 🔔 for new activity</span>
            </li>
          </ul>
        </div>

        {/* Table of Contents */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-12 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">In This Guide:</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <a href="#navigation" className="text-blue-600 hover:text-blue-700 hover:underline">→ Getting Around</a>
            <a href="#uploading" className="text-blue-600 hover:text-blue-700 hover:underline">→ Uploading Photos</a>
            <a href="#viewing" className="text-blue-600 hover:text-blue-700 hover:underline">→ Viewing & Interacting</a>
            <a href="#tags" className="text-blue-600 hover:text-blue-700 hover:underline">→ Using Tags</a>
            <a href="#notifications" className="text-blue-600 hover:text-blue-700 hover:underline">→ Notifications</a>
            <a href="#reactions" className="text-blue-600 hover:text-blue-700 hover:underline">→ Reactions & Comments</a>
            <a href="#sharing" className="text-blue-600 hover:text-blue-700 hover:underline">→ Sharing Photos</a>
            <a href="#deleting" className="text-blue-600 hover:text-blue-700 hover:underline">→ Deleting Content</a>
            <a href="#privacy" className="text-blue-600 hover:text-blue-700 hover:underline">→ Privacy & Safety</a>
          </div>
        </div>

        {/* Navigation */}
        <section id="navigation" className="mb-12">
          <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <span>🧭</span> Getting Around
            </h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">The Menu Bar</h3>
                <p className="text-gray-700 mb-4">
                  At the top of every page, you'll find the main menu. Here's what each option does:
                </p>
                <div className="space-y-3 ml-4">
                  <div>
                    <strong className="text-gray-900">📸 Photos:</strong>
                    <span className="text-gray-700"> Your main feed - all family photos in one place</span>
                  </div>
                  <div>
                    <strong className="text-gray-900">🏷️ Tags:</strong>
                    <span className="text-gray-700"> Browse photos by person, place, or event</span>
                  </div>
                  <div>
                    <strong className="text-gray-900">👤 Admin:</strong>
                    <span className="text-gray-700"> Manage users and approve new family members (admins only)</span>
                  </div>
                  <div>
                    <strong className="text-gray-900">📖 Tutorial:</strong>
                    <span className="text-gray-700"> This helpful guide you're reading now!</span>
                  </div>
                  <div>
                    <strong className="text-gray-900">ℹ️ About:</strong>
                    <span className="text-gray-700"> Learn about our family album's purpose</span>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>💡 Tip:</strong> On mobile phones, tap the menu icon (☰) in the top left to see all options.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">The Notification Bell 🔔</h3>
                <p className="text-gray-700">
                  In the top right corner, the bell icon shows when someone interacts with your photos or mentions you. 
                  A red badge with a number means you have new notifications waiting.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Uploading Photos */}
        <section id="uploading" className="mb-12">
          <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <span>📤</span> Uploading Photos
            </h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">How to Upload</h3>
                <ol className="space-y-3 ml-4 list-decimal list-inside text-gray-700">
                  <li>Click the colourful <strong>"+ Upload"</strong> button in the top right</li>
                  <li>Choose one or more photos from your device</li>
                  <li>If needed, you can crop the image before uploading (drag the corners to adjust)</li>
                  <li>Add a caption to describe the photo (optional but helpful!)</li>
                  <li>Add tags to help people find the photo later (more on tags below)</li>
                  <li>Click <strong>"Upload"</strong> and your photo will appear in the feed</li>
                </ol>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800">
                  <strong>💡 Best Practice:</strong> Add tags like "Jason Coleman" or "Cashel 1985" so family members 
                  can easily find photos of specific people, places, or times.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">What Makes a Good Photo?</h3>
                <ul className="space-y-2 ml-4 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span>✓</span> Clear and well-lit images work best
                  </li>
                  <li className="flex items-start gap-2">
                    <span>✓</span> Old photos are just as welcome as new ones
                  </li>
                  <li className="flex items-start gap-2">
                    <span>✓</span> Photos of gatherings, celebrations, and everyday moments all matter
                  </li>
                  <li className="flex items-start gap-2">
                    <span>✓</span> Even if you're not sure who's in the photo - someone else might know!
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Viewing & Interacting */}
        <section id="viewing" className="mb-12">
          <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <span>👀</span> Viewing & Interacting with Photos
            </h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Opening a Photo</h3>
                <p className="text-gray-700 mb-4">
                  Click on any photo thumbnail to see it full size. You'll see:
                </p>
                <ul className="space-y-2 ml-4 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span>•</span> The full-size image
                  </li>
                  <li className="flex items-start gap-2">
                    <span>•</span> Who uploaded it and when
                  </li>
                  <li className="flex items-start gap-2">
                    <span>•</span> Tags showing people, places, or events
                  </li>
                  <li className="flex items-start gap-2">
                    <span>•</span> A ❤️ button to like the photo
                  </li>
                  <li className="flex items-start gap-2">
                    <span>•</span> Comments from family members
                  </li>
                  <li className="flex items-start gap-2">
                    <span>•</span> Audio messages (if any have been recorded)
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Liking Photos</h3>
                <p className="text-gray-700">
                  Click the heart icon (❤️) in the <strong>bottom-right corner</strong> of a photo to like it. 
                  It's a simple way to say "I love this!" The person who uploaded it will get a notification 
                  that you liked their photo.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Adding Comments</h3>
                <p className="text-gray-700 mb-3">
                  Comments are the heart of this album - they let you share memories and stories. 
                  <strong>Your comments help others understand what was happening</strong> in a photo. 
                  Maybe you remember the story behind the moment, or know where it was taken, or can explain 
                  why everyone's laughing. Share that context!
                </p>
                <ol className="space-y-2 ml-4 list-decimal list-inside text-gray-700">
                  <li>Scroll down below the photo to the comment box</li>
                  <li>Type your message (up to 2,000 characters)</li>
                  <li>Type @ followed by a name to mention someone (they'll get notified)</li>
                  <li>Click "Post Comment" to share</li>
                </ol>
                
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mt-4">
                  <p className="text-sm text-indigo-800">
                    <strong>💬 What Makes a Great Comment:</strong> Add context! Tell us when and where the photo 
                    was taken, what was happening that day, or why this moment mattered. Future generations will 
                    treasure these stories. Even a simple "This was taken at Mam's house, Christmas 1992" adds 
                    valuable information.
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Recording Audio Messages 🎤</h3>
                <p className="text-gray-700 mb-3">
                  Want to share a story or memory about a photo in your own voice? Maybe Robbie can put that 
                  amazing voice to good use!
                </p>
                <ol className="space-y-2 ml-4 list-decimal list-inside text-gray-700">
                  <li>Open a photo</li>
                  <li>Look in the details panel for the "Audio Messages" section</li>
                  <li>Click the "Record Audio Message" button</li>
                  <li>Allow your browser to use your microphone (you'll see a pop-up)</li>
                  <li>Click "Start Recording" and speak naturally - you have <strong>20 seconds</strong></li>
                  <li>Click "Stop" when you're done</li>
                  <li>Listen to your recording and click "Upload" if you're happy with it</li>
                </ol>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mt-4">
                  <p className="text-sm text-purple-800">
                    <strong>💡 Audio Tips:</strong> Find a quiet spot, speak clearly, and don't worry about being perfect. 
                    Your voice is a gift to future generations! No "Ave Maria's" though - you've only got 20 seconds, 
                    and you can only leave <strong>one message per photo</strong>. Make it count!
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Tags */}
        <section id="tags" className="mb-12">
          <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <span>🏷️</span> Using Tags
            </h2>
            
            <div className="space-y-6">
              <p className="text-gray-700">
                Tags help organise photos so family members can easily find images of specific people, 
                places, or events. Think of them like labels in a filing system - but here's the key: 
                <strong> tagging is a team sport!</strong> The more we all tag photos, the easier it becomes 
                for everyone to find memories.
              </p>

              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-lg p-5">
                <h3 className="text-lg font-bold text-gray-900 mb-2">🏆 Why Tagging Matters</h3>
                <p className="text-gray-700 mb-3">
                  When you tag a photo, you're not just organising it for yourself - you're helping the entire family! 
                  Maybe you remember that a photo was taken in "Cashel 1987" or you can identify "Auntie Margaret" 
                  in the background. That knowledge is valuable! Add those tags so others can find these memories too.
                </p>
                <p className="text-gray-700 font-medium">
                  💡 Everyone can tag anyone's photos - so if you spot your mam in someone else's upload, go ahead and tag her!
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Types of Tags</h3>
                <div className="space-y-3 ml-4">
                  <div>
                    <strong className="text-gray-900">👤 People:</strong>
                    <span className="text-gray-700"> Names of family members in the photo ("Jason Coleman", "Uncle John")</span>
                  </div>
                  <div>
                    <strong className="text-gray-900">📍 Places:</strong>
                    <span className="text-gray-700"> Locations where photos were taken ("Cashel", "Mam's Kitchen")</span>
                  </div>
                  <div>
                    <strong className="text-gray-900">🎉 Events:</strong>
                    <span className="text-gray-700"> Occasions or celebrations ("Christmas 1995", "Tom's Wedding")</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Adding Tags to Photos</h3>
                <ol className="space-y-2 ml-4 list-decimal list-inside text-gray-700">
                  <li>Open a photo (yours or anyone's)</li>
                  <li>Look in the details panel (right side on desktop, below image on mobile)</li>
                  <li>Click the "Add Tag" button in the Tags section</li>
                  <li>
                    <strong>Start typing the tag name</strong> - existing tags will <strong>autocomplete</strong> to help you. 
                    This is important! Using autocomplete ensures consistency, making it easier for everyone to search
                  </li>
                  <li>Select the type (Person, Place, or Event)</li>
                  <li>Click "✓ Add Tag" to save it - the tag now appears on the photo</li>
                </ol>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>💡 Pro Tip:</strong> When you start typing, watch for the autocomplete suggestions. If you see 
                  "Auntie Margaret" already exists, use that instead of creating "Aunt Margaret" or "Margaret." 
                  Consistency helps everyone search more effectively!
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">🔍 Searching & Finding Photos</h3>
                <p className="text-gray-700 mb-3">
                  This is where all that tagging pays off! There are several ways to find photos:
                </p>
                
                <div className="space-y-3 ml-4">
                  <div>
                    <strong className="text-gray-900">Browse by Tags:</strong>
                    <ol className="space-y-1 ml-4 list-decimal list-inside text-gray-700 mt-2">
                      <li>Click "Tags" in the top menu</li>
                      <li>You'll see all tags organised by type (People, Places, Events)</li>
                      <li>Click any tag to see all photos with that label</li>
                      <li>Use the filters to show only the type you're interested in</li>
                    </ol>
                  </div>
                  
                  <div>
                    <strong className="text-gray-900">Search for Specific Tags:</strong>
                    <p className="text-gray-700 ml-4 mt-2">
                      When you're on the Tags page, you can search for a specific person, place, or event. 
                      Just start typing and the list will filter to show matches. This is especially handy 
                      when there are lots of tags!
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <strong>💡 Helpful Hint:</strong> Don't worry about getting tag names "perfect." If you tag someone as 
                  "Jason Coleman Phillips" and someone else tagged him as just "Jason Coleman," both tags help people find the photos. 
                  But do check the autocomplete first - if a tag already exists, use it!
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Notifications */}
        <section id="notifications" className="mb-12">
          <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <span>🔔</span> Notifications
            </h2>
            
            <div className="space-y-6">
              <p className="text-gray-700">
                The bell icon in the top right corner keeps you updated when someone interacts with your content 
                or mentions you in a comment.
              </p>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">What Triggers Notifications</h3>
                <ul className="space-y-3 ml-4 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span>❤️</span> <span>Someone likes your photo</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>💬</span> <span>Someone comments on your photo</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>@</span> <span>Someone mentions you in a comment</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>👤</span> <span>Someone tags you in a photo</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>😊</span> <span>Someone reacts to your comment with an emoji</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>🔗</span> <span>Someone shares a photo with you</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Checking Your Notifications</h3>
                <ol className="space-y-2 ml-4 list-decimal list-inside text-gray-700">
                  <li>Look for the bell icon 🔔 in the top right</li>
                  <li>A red badge with a number shows how many new notifications you have</li>
                  <li>Click the bell to see your recent notifications</li>
                  <li>Click any notification to go directly to that photo</li>
                  <li>Click "Mark all read" to clear the notification badge</li>
                </ol>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>💡 Good to Know:</strong> Notifications update automatically every 15 seconds, so you'll see 
                  new activity without having to refresh the page. The site keeps you in the loop!
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Reactions */}
        <section id="reactions" className="mb-12">
          <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <span>😊</span> Reactions & Comment Emojis
            </h2>
            
            <div className="space-y-6">
              <p className="text-gray-700">
                Sometimes a simple emoji says it all! You can react to comments with emojis to show support, 
                share a laugh, or acknowledge a memory.
              </p>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">How to React to Comments</h3>
                <ol className="space-y-2 ml-4 list-decimal list-inside text-gray-700">
                  <li>Find a comment you want to react to</li>
                  <li>Look for the small "+" button next to the comment</li>
                  <li>Click it to see available emoji reactions</li>
                  <li>Choose an emoji (❤️, 😂, 👍, 🎉, etc.)</li>
                  <li>The person who wrote the comment gets notified</li>
                </ol>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Changing or Removing Reactions</h3>
                <ul className="space-y-2 ml-4 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span>•</span> Click the same emoji again to remove your reaction
                  </li>
                  <li className="flex items-start gap-2">
                    <span>•</span> Click a different emoji to change your reaction
                  </li>
                  <li className="flex items-start gap-2">
                    <span>•</span> Hover over an emoji to see who reacted
                  </li>
                </ul>
              </div>

              <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
                <p className="text-sm text-pink-800">
                  <strong>💡 Why Reactions Matter:</strong> They're a quick way to acknowledge someone's story without 
                  writing a full comment. Even a simple ❤️ can mean "I remember that too" or "Thank you for sharing."
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Sharing */}
        <section id="sharing" className="mb-12">
          <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <span>🔗</span> Sharing Photos with Family
            </h2>
            
            <div className="space-y-6">
              <p className="text-gray-700">
                Want to make sure someone sees a particular photo? Use the Share feature to send them a direct notification.
              </p>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">How to Share a Photo</h3>
                <ol className="space-y-2 ml-4 list-decimal list-inside text-gray-700">
                  <li>Open the photo you want to share</li>
                  <li>Look for the Share button (🔗 link icon) in the <strong>bottom-right corner</strong> of the photo</li>
                  <li>Click the Share button</li>
                  <li>Select one or more family members from the list</li>
                  <li>Add an optional message like "Remember this day?" or "Check this out!"</li>
                  <li>Click "Share" - they'll get a notification immediately</li>
                </ol>
              </div>

              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                <p className="text-sm text-indigo-800">
                  <strong>💡 When to Share:</strong> Use sharing when you find a photo someone might not have seen, 
                  or when you want to start a conversation about a memory. It's like saying "Hey, look at this!"
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Deleting Content */}
        <section id="deleting" className="mb-12">
          <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <span>🗑️</span> Deleting Content
            </h2>
            
            <div className="space-y-6">
              <p className="text-gray-700">
                Made a mistake? Need to remove something? Here's what you can delete and how.
              </p>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">What You Can Delete</h3>
                <div className="space-y-3 ml-4">
                  <div>
                    <strong className="text-gray-900">✓ Your own photos:</strong>
                    <span className="text-gray-700"> Any photo you uploaded</span>
                  </div>
                  <div>
                    <strong className="text-gray-900">✓ Your own comments:</strong>
                    <span className="text-gray-700"> Any comment or audio message you posted</span>
                  </div>
                  <div>
                    <strong className="text-gray-900">✓ Your own reactions:</strong>
                    <span className="text-gray-700"> Emoji reactions you've added to comments</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">How to Delete</h3>
                
                <div className="mb-4">
                  <strong className="text-gray-900">Deleting a Photo:</strong>
                  <ol className="space-y-1 ml-4 list-decimal list-inside text-gray-700 mt-2">
                    <li>Open the photo (or hover over it in the feed)</li>
                    <li>Look for the red recycle bin icon 🗑️ in the top-right corner</li>
                    <li>Click the bin icon</li>
                    <li>Confirm you want to delete it</li>
                  </ol>
                </div>

                <div className="mb-4">
                  <strong className="text-gray-900">Deleting a Comment:</strong>
                  <ol className="space-y-1 ml-4 list-decimal list-inside text-gray-700 mt-2">
                    <li>Find your comment</li>
                    <li>Click the small recycle bin icon 🗑️ next to it</li>
                    <li>Confirm the deletion</li>
                  </ol>
                </div>

                <div className="mb-4">
                  <strong className="text-gray-900">Deleting an Audio Message:</strong>
                  <ol className="space-y-1 ml-4 list-decimal list-inside text-gray-700 mt-2">
                    <li>Find your audio message below the photo</li>
                    <li>Click the small recycle bin icon 🗑️ next to it</li>
                    <li>Confirm the deletion</li>
                  </ol>
                </div>

                <div>
                  <strong className="text-gray-900">Removing a Reaction:</strong>
                  <p className="text-gray-700 ml-4 mt-2">
                    Simply click the emoji again to remove your reaction.
                  </p>
                </div>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800">
                  <strong>⚠️ Important:</strong> When you delete a photo, it's removed for everyone and can't be recovered. 
                  Comments, tags, and audio messages attached to that photo are also deleted. Be certain before you delete!
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Privacy */}
        <section id="privacy" className="mb-12">
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl shadow-xl p-8 sm:p-10 border-2 border-green-200">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <span>🔒</span> Privacy & Safety
            </h2>
            
            <div className="space-y-6">
              <p className="text-gray-700 text-lg">
                This is a private family space. Only approved family members can access these photos.
              </p>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Who Can See What?</h3>
                <div className="space-y-3 ml-4">
                  <div>
                    <strong className="text-gray-900">✓ All family members</strong>
                    <span className="text-gray-700"> can see all photos, comments, and tags</span>
                  </div>
                  <div>
                    <strong className="text-gray-900">✓ Only you</strong>
                    <span className="text-gray-700"> can delete your own photos and comments</span>
                  </div>
                  <div>
                    <strong className="text-gray-900">✓ Administrators</strong>
                    <span className="text-gray-700"> can help with technical issues and approve new members</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Your Rights</h3>
                <ul className="space-y-2 ml-4 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span>✓</span> You can delete any photo you've uploaded at any time
                  </li>
                  <li className="flex items-start gap-2">
                    <span>✓</span> You can remove yourself from tags if you prefer
                  </li>
                  <li className="flex items-start gap-2">
                    <span>✓</span> You can delete your own comments and audio messages
                  </li>
                  <li className="flex items-start gap-2">
                    <span>✓</span> You control what you share - there's never any pressure to post
                  </li>
                </ul>
              </div>

              <div className="bg-white border-2 border-green-300 rounded-lg p-6 mt-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">⚠️ Important: If You Want a Photo Removed</h3>
                <div className="space-y-3 text-gray-700">
                  <p>
                    <strong>If you see a photo of yourself that you'd like taken down:</strong>
                  </p>
                  <ul className="space-y-2 ml-4 list-disc list-inside">
                    <li>
                      <strong>You uploaded it?</strong> You can delete it yourself (see "Deleting Content" above)
                    </li>
                    <li>
                      <strong>Someone else uploaded it?</strong> Contact me (Jay) immediately at{' '}
                      <a href="mailto:jaycoleman@gmail.com" className="text-blue-600 hover:text-blue-700 underline">
                        jaycoleman@gmail.com
                      </a>
                      {' '}and I'll remove it right away
                    </li>
                  </ul>
                  <p className="mt-4">
                    <strong>Your comfort and privacy matter most.</strong> This is meant to be a positive space 
                    where everyone feels respected. If something makes you uncomfortable, please say so - no questions asked.
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Being Respectful</h3>
                <p className="text-gray-700 mb-3">
                  This album works best when we all look out for each other:
                </p>
                <ul className="space-y-2 ml-4 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span>•</span> Consider how others might feel before posting photos of them
                  </li>
                  <li className="flex items-start gap-2">
                    <span>•</span> Keep comments kind and supportive
                  </li>
                  <li className="flex items-start gap-2">
                    <span>•</span> If someone asks you to remove or re-tag a photo, please do so
                  </li>
                  <li className="flex items-start gap-2">
                    <span>•</span> Remember: this is for preserving happy memories and bringing us together
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Getting Help */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl shadow-xl p-8 border border-purple-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span>💬</span> Need Help?
          </h2>
          <p className="text-gray-700 mb-4">
            Don't worry if something doesn't make sense right away - you'll get the hang of it! 
            If you're stuck or have questions:
          </p>
          <ul className="space-y-3 text-gray-700">
            <li className="flex items-start gap-2">
              <span>📧</span>
              <span>
                Email Jay at{' '}
                <a href="mailto:jaycoleman@gmail.com" className="text-blue-600 hover:text-blue-700 underline font-medium">
                  jaycoleman@gmail.com
                </a>
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span>👨‍👩‍👧‍👦</span>
              <span>Ask another family member - many of us are here to help!</span>
            </li>
            <li className="flex items-start gap-2">
              <span>🔍</span>
              <span>Come back to this tutorial anytime by clicking "Tutorial" in the menu</span>
            </li>
          </ul>
        </div>

        {/* Closing */}
        <div className="mt-12 text-center">
          <p className="text-xl text-gray-700 italic max-w-2xl mx-auto">
            Thank you for being part of preserving our family's story. Your photos, comments, 
            and voices are gifts to future generations. 💙
          </p>
        </div>
      </div>
    </div>
  );
}

